import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { throwError, Observable, from } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ICustomErrorApi } from '@core-lib/models';
import { HttpErrorMessage } from '@core-lib/classes';
import { Store } from '@ngrx/store';
import { AppLanguage } from '@core-lib/enums';
import { SharedStoreSelectors } from '@core-lib/store/shared-store';

export class ErrorInterceptor implements HttpInterceptor {
	currentLanguage: AppLanguage;

	constructor(
		private translateService: TranslateService,
		private store$: Store
	) {
		this.store$.select(SharedStoreSelectors.selectLanguage).subscribe(language => {
			this.currentLanguage = language;
		});
	}

	intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		return next.handle(request).pipe(
			catchError((err: HttpErrorResponse) => {
				return this.manageHttpErrorResponse(err);
			})
		);
	}

	manageHttpErrorResponse(error: HttpErrorResponse): Observable<HttpEvent<any>> {
		let errorMsg = '';
		if (error.error instanceof ErrorEvent) {
			// Client-side error
			errorMsg = `Error: ${error.error.message}`;
		} else {
			// Server-side error
			errorMsg = `Error Code: ${error.status}\nMessage: ${error.message}`;
		}

		const errorMessage = new HttpErrorMessage(error.message);

		// Client Side Error
		if (error.error instanceof ErrorEvent) {
			errorMessage.appendKey('DefaultClient').withParams({ message: error.error.message });

			// Server Side Error
		} else if (error.error) {
			if (error.error instanceof Blob) {
				// It's a blob !! Read it and create a clone of the error with the read text.
				return from(
					new Promise<any>((resolve, reject) => {
						const reader = new FileReader();
						reader.onload = (e: Event) => {
							try {
								const errmsg = JSON.parse((<any>e.target).result);
								const cloneError = new HttpErrorResponse({
									error: errmsg,
									headers: error.headers,
									status: error.status,
									statusText: error.statusText,
									url: error.url
								});
								return resolve(cloneError);
							} catch (e) {
								reject(error);
							}
						};
						reader.onerror = e => {
							reject(error);
						};
						reader.readAsText(error.error);
					})
				).pipe(
					switchMap(input => {
						return this.manageHttpErrorResponse(input);
					})
				);
			}

			// Custom error unhandled
			if (!error.error.errorCode || !this.handleCustomError(error.error, errorMessage)) {
				// Generic status code error
				if (!!error.status) {
					switch (error.status) {
						case 400:
							errorMessage.appendKey(error.error.error === 'invalid_grant' ? 'InvalidGrant' : error.status);
							break;
						case 401:
						case 402:
						case 403:
						case 404:
						case 500:
							errorMessage.appendKey(error.status);
							break;
						case 501:
							errorMessage.appendKey('NotImplemented');
							break;
						default:
							errorMessage.appendKey('DefaultServer');
					}

					// Unknown error
				} else {
					errorMessage.appendKey('UnknownServer');
				}
			}

			// Default error
		} else {
			errorMessage
				.fromCode(error.status)
				.appendKey('DefaultServer')
				.withParams({ message: `${error.message} (${error.status})` });
		}

		errorMessage.translate(this.translateService);

		if (error.error && error.error.isManagedError) {
			errorMessage.translated = error.error.detail;
			errorMessage.code = error.error.code;
		}

		if (error.error && error.error.extraData) {
			errorMessage.extraData = error.error.extraData;
		}

		if (error.error && error.error.specificError) {
			errorMessage.specificError = error.error.specificError;
		}

		return throwError(errorMessage);
	}

	handleCustomError(customError: ICustomErrorApi, errorMessage: HttpErrorMessage): boolean {
		const specificError = this.getSpecificErrorCode(customError);

		if (this.hasTranslation(specificError)) {
			errorMessage.appendKey(specificError);
			return true;
		} else if (this.hasTranslation(customError.errorCode)) {
			errorMessage.asFunctional().appendKey(customError.errorCode).withParams({ message: customError.errorMessage });
			return true;
		}

		return false;
	}

	getSpecificErrorCode(customError: ICustomErrorApi): string {
		return customError.specificError.split('.').pop();
	}

	hasTranslation(key: string): boolean {
		const translations = this.translateService.translations;
		return !!this.translateService.parser.getValue(translations[this.currentLanguage], 'Errors.' + key);
	}
}
